/**
 * Die Regeln des Hauptmenues (Masterprompt Abschnitt 88).
 *
 * Hier steht, WAS ein Knopf ausloest - nicht, wie er aussieht. Dadurch laesst
 * sich der ganze Weg vom Menue in ein Match pruefen, ohne ein Bild zu
 * zeichnen: Welcher Bildschirm folgt auf welchen, welcher Startbefehl kommt
 * heraus, und wann gar keiner herauskommen darf.
 *
 * Der Aufbau folgt der Produktentscheidung vom 13.10.2026:
 *
 *   Start ---> Multiplayer ---> Quickmatch / Create Match / Join Match
 *         |
 *         +-> Singleplayer --> Staerke und Linebound des Gegners
 *         |
 *         +-> Settings -----> Ton und Vollbild
 *         |
 *         +-> Subscribe to Playtest (Verweis auf die Homepage)
 *
 * Der Zugangscode des geschlossenen Playtests liegt NICHT hier, sondern davor
 * (Zugangstor, 04.10.2026): Wer keinen hat, sieht dieses Menue gar nicht.
 */

import { isValidMatchCode } from '@dueline/shared';
import type { BotDifficulty } from '@dueline/game-data';
import type { JoinMode } from '../net/MatchConnection.js';

/** Die Bildschirme des Menues. */
export type MenuBildschirm = 'start' | 'multiplayer' | 'singleplayer' | 'settings';

/**
 * Alles, was der Client braucht, um ein Match zu beginnen.
 *
 * Es ist bewusst dasselbe Format fuer alle vier Wege - der Aufrufer muss
 * nicht wissen, aus welchem Bildschirm es kam.
 */
export interface Startbefehl {
  mode: JoinMode;
  /**
   * Doppelt belegt, und das ist Absicht des bestehenden Protokolls: Beim
   * Beitreten steht hier der LOBBYCODE, beim Testgegner seine STAERKE.
   */
  code: string;
  /** Der eigene Linebound. */
  heroId: string;
  /** Der Linebound des Testgegners. Leer heisst: Der Server wuerfelt. */
  botHeroId: string;
}

/** Zurueck fuehrt immer auf den Startbildschirm - tiefer geht das Menue nicht. */
export function zurueckVon(bildschirm: MenuBildschirm): MenuBildschirm {
  void bildschirm;
  return 'start';
}

/**
 * Der Startbefehl fuer ein Match gegen Menschen.
 *
 * Gibt null zurueck, wenn es (noch) nicht losgehen darf - dann bleibt der
 * Knopf gesperrt, statt ins Leere zu laufen:
 *
 *   - waehrend schon eine Verbindung aufgebaut wird (sonst zwei Beitritte),
 *   - beim Beitreten ohne vollstaendigen Lobbycode.
 */
export function multiplayerStart(eingabe: {
  art: 'quick' | 'create' | 'join';
  /** Nur beim Beitreten gebraucht. */
  code: string;
  verbindet: boolean;
}): Startbefehl | null {
  if (eingabe.verbindet) return null;
  if (eingabe.art === 'join' && !isValidMatchCode(eingabe.code)) return null;

  return {
    mode: eingabe.art,
    code: eingabe.art === 'join' ? eingabe.code : '',
    /*
     * OHNE Linebound (Produktentscheidung vom 13.10.2026): Gegen Menschen
     * waehlt man ihn in der LOBBY, wo eine Minute Zeit dafuer ist und man
     * sieht, ob der andere schon so weit ist.
     *
     * Der Server macht daraus eine Vorbelegung und merkt sich, dass sie
     * niemand bestaetigt hat - laeuft die Zeit ab, wuerfelt er.
     */
    heroId: '',
    botHeroId: '',
  };
}

/**
 * Der Startbefehl fuer ein Match gegen den Testgegner.
 *
 * Die Staerke reist im Feld "code" - so verlangt es der bestehende Beitritt.
 * Diese Eigenheit steht deshalb hier an einer Stelle und nicht verstreut in
 * der Oberflaeche.
 */
export function singleplayerStart(eingabe: {
  heroId: string;
  /** Leer heisst: Der Server wuerfelt den Gegner aus. */
  botHeroId: string;
  stufe: BotDifficulty;
  verbindet: boolean;
}): Startbefehl | null {
  if (eingabe.verbindet) return null;

  return {
    mode: 'bot',
    code: eingabe.stufe,
    heroId: eingabe.heroId,
    botHeroId: eingabe.botHeroId,
  };
}
